/**
 * SavoirCareCheckinPanel.jsx — front-desk CareStart check-in drawer.
 *
 * What: A drawer launched from the board header that lets front-desk staff add a
 *       patient to the Incoming column by entering/scanning the patient's CareStart
 *       code (the share token id) or pasting the QR payload. Calls the
 *       checkInPatientFromCareStart callable.
 * Why:  Patients arriving with a CareStart code are checked in BEFORE the clinician
 *       bedside redeem, so the ED sees them in Incoming.
 * Invariants:
 *   - The token is NOT consumed here (the callable only reads it); the clinician
 *     bedside redeem is what sets used: true.
 *   - facilityId is resolved by the board hook from the signed-in user, never typed.
 *   - Error states surfaced to staff: not found, expired, wrong facility.
 *
 * Ported from HealthDesk Experimental (Exp-B). MinusOne adaptations:
 *   - Uses the compat callable surface (`firebaseApp.functions().httpsCallable`)
 *     to match app conventions (services/clinicService.js), instead of the modular
 *     `firebase/functions` getFunctions/httpsCallable.
 *   - Inlines a small callable-error message resolver (Exp-B's shared util is not
 *     present in MinusOne).
 * Tightest coupling: checkInPatientFromCareStart callable, SavoirCareBoardPage.jsx.
 */

import React, { useState, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { CloseRounded, QrCodeScannerRounded } from '@mui/icons-material';
import firebaseApp from 'services/firebase';

/** Drawer width on desktop; full-width on phones. */
const DRAWER_WIDTH = 380;

/**
 * Resolve a user-facing message from a Firebase callable error, preferring the
 * server-provided HttpsError message.
 * @param {*} err the thrown error
 * @returns {string} a message safe to show front-desk staff
 */
function resolveCallableErrorMessage(err) {
  if (!err) return 'Could not check this patient in.';
  if (typeof err.message === 'string' && err.message.trim()) return err.message;
  return 'Could not check this patient in.';
}

/**
 * Extract the token id from raw input. Front-desk staff may paste either the bare
 * token id or the scanned QR payload (a JSON object `{ type, tokenId, ... }`).
 * @param {string} raw the text field value
 * @returns {string} the resolved token id (may be empty)
 */
export function resolveTokenId(raw) {
  const value = (raw || '').trim();
  if (!value) return '';
  // QR payloads are JSON containing a tokenId field; tolerate a pasted payload.
  if (value.startsWith('{')) {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed.tokenId === 'string') return parsed.tokenId.trim();
    } catch (e) {
      // Not JSON after all — fall through and treat the text as the token id.
      console.warn('[SavoirCare] Check-in input looked like JSON but did not parse:', e);
    }
  }
  return value;
}

const SavoirCareCheckinPanel = ({ open, facilityId, onClose }) => {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const reset = useCallback(() => {
    setCode('');
    setError(null);
    setSuccess(null);
  }, []);

  const handleClose = useCallback(() => {
    if (busy) return;
    reset();
    onClose?.();
  }, [busy, reset, onClose]);

  const handleCheckIn = useCallback(async () => {
    const tokenId = resolveTokenId(code);
    if (!tokenId) {
      setError('Enter or scan the patient’s CareStart code.');
      return;
    }
    if (!facilityId) {
      setError('No facility is associated with your account.');
      return;
    }
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const checkIn = firebaseApp.functions().httpsCallable('checkInPatientFromCareStart');
      const { data } = await checkIn({ tokenId, facilityId });
      if (data?.alreadyExists) {
        setSuccess('This patient is already on the board.');
      } else {
        const summary = data?.chiefComplaintSummary ? ` — ${data.chiefComplaintSummary}` : '';
        setSuccess(`Patient added to Incoming${summary}`);
      }
      setCode('');
    } catch (err) {
      console.error('[SavoirCare] Check-in failed:', err);
      setError(resolveCallableErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }, [code, facilityId]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: DRAWER_WIDTH }, p: 2.5 },
        'data-testid': 'savoir-care-checkin-panel',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">Front-desk check-in</Typography>
        <IconButton onClick={handleClose} disabled={busy} aria-label="Close check-in panel">
          <CloseRounded />
        </IconButton>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Scan the patient’s CareStart QR code or enter the code they present. The patient is added
        to the Incoming column; their session opens fully when the clinician scans at the bedside.
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <TextField
        label="CareStart code or QR"
        placeholder="Enter the patient’s code"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        fullWidth
        multiline
        minRows={1}
        maxRows={4}
        disabled={busy}
        sx={{ mb: 2 }}
        inputProps={{ 'data-testid': 'savoir-care-checkin-code' }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleCheckIn();
          }
        }}
      />

      <LoadingButton
        variant="contained"
        fullWidth
        loading={busy}
        startIcon={<QrCodeScannerRounded />}
        onClick={handleCheckIn}
        disabled={!code.trim()}
        data-testid="savoir-care-checkin-submit"
      >
        Check in patient
      </LoadingButton>

      {success && (
        <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
    </Drawer>
  );
};

export default SavoirCareCheckinPanel;
